/**
 * Notification API — TanStack Query hooks for the caller's in-app inbox:
 * a polled list (optionally unread-only), mark-one-read, and mark-all-read.
 * Uses the shared `api` instance (base '/api/v1', envelope-normalised errors).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { NotificationList } from '@/types/notification';

interface ApiEnvelope<T> {
  data: T;
  error: { code: string; message: string } | null;
}

function invalidateNotificationQueries(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['notification'] });
}

// ---------------------------------------------------------------------------
// Query — the caller's notification inbox, polled for the top-nav bell
// ---------------------------------------------------------------------------

export function useNotifications(unreadOnly?: boolean) {
  const unread = !!unreadOnly;
  return useQuery({
    queryKey: ['notification', { unread }],
    queryFn: () =>
      api
        .get<ApiEnvelope<NotificationList>>('/notification', { params: { unread } })
        .then((res) => res.data.data),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Mutation — mark one notification read
// ---------------------------------------------------------------------------

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      api.patch<ApiEnvelope<void>>(`/notification/${id}/read`).then(() => undefined),
    onSuccess: () => {
      invalidateNotificationQueries(qc);
    },
  });
}

// ---------------------------------------------------------------------------
// Mutation — mark every one of the caller's notifications read
// ---------------------------------------------------------------------------

export interface MarkAllNotificationsReadResult {
  updated: number;
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api
        .post<ApiEnvelope<MarkAllNotificationsReadResult>>('/notification/read-all')
        .then((res) => res.data.data),
    onSuccess: () => {
      invalidateNotificationQueries(qc);
    },
  });
}
